/**
 * MergeConfigAndPropsTask - об'єднує конфігурації та props
 *
 * Використовується для об'єднання глобальних конфігурацій
 * з локальними props компонента з урахуванням features
 */

import { CheckNodeHelperTask } from './CheckNodeHelperTask'

/**
 * Перевіряє чи є значення простим об'єктом
 */
function isObject(item: any): boolean {
    return (
        item &&
        typeof item === 'object' &&
        !Array.isArray(item) &&
        !CheckNodeHelperTask.isReactComponent(item) &&
        !CheckNodeHelperTask.isObserver(item) &&
        !CheckNodeHelperTask.isElement(item) &&
        // Додаємо перевірку на екземпляри класів (не є простим Object)
        // Якщо конструктор не є Object - це екземпляр класу
        (!item.constructor || item.constructor === Object)
    )
}

/**
 * Глибоко об'єднує об'єкти з захистом від циклічних посилань
 */
function mergeDeep(target: any, ...sources: any[]): any {
    return mergeDeepWithTracking(new WeakSet(), target, ...sources)
}

/**
 * Внутрішня функція для глибокого об'єднання з відстеженням оброблених об'єктів
 */
function mergeDeepWithTracking(seen: WeakSet<object>, target: any, ...sources: any[]): any {
    if (!sources.length) return target

    const source = sources.shift()
    
    // Захист від циклічних посилань - не мержимо той самий об'єкт
    if (target === source) {
        return mergeDeepWithTracking(seen, target, ...sources)
    }

    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            const sourceValue = source[key]
            
            // Пропускаємо функції - просто копіюємо їх
            if (typeof sourceValue === 'function') {
                target[key] = sourceValue
                continue
            }
            
            // Якщо значення - це об'єкт
            if (isObject(sourceValue)) {
                // Якщо це той самий об'єкт, що вже є в target - пропускаємо
                if (target[key] === sourceValue) {
                    continue
                }
                
                // Перевіряємо на циклічне посилання
                const isCircular = seen.has(sourceValue)
                
                if (isCircular) {
                    // Якщо це циклічне посилання - копіюємо по посиланню
                    target[key] = sourceValue
                    continue
                }
                
                // Додаємо до відстежуваних перед рекурсією
                seen.add(sourceValue)
                
                // Якщо target[key] не існує або не є об'єктом - створюємо новий
                if (!isObject(target[key])) {
                    target[key] = {}
                }
                
                // Рекурсивно мержимо
                mergeDeepWithTracking(seen, target[key], sourceValue)
            } else if (
                // Якщо sourceValue це екземпляр класу (не простий Object)
                sourceValue &&
                typeof sourceValue === 'object' &&
                !Array.isArray(sourceValue) &&
                sourceValue.constructor &&
                sourceValue.constructor !== Object
            ) {
                // Копіюємо екземпляр класу по ссылці без мерджу
                target[key] = sourceValue
            } else {
                // Примітивні значення просто копіюємо
                target[key] = sourceValue
            }
        }
    }

    return mergeDeepWithTracking(seen, target, ...sources)
}

/**
 * Об'єднує конфігурації з урахуванням features
 */
export const MergeConfigAndPropsTask = (configs: Record<string, any>): Record<string, any> | undefined => {
    const objects: any[] = []

    Object.keys(configs).forEach((keyConfig) => {
        const config = configs[keyConfig]

        if (config?.features) {
            Object.keys(config.features).forEach((keyFeature) => {
                const feature = config.features[keyFeature]
                // Не используем spread, чтобы избежать циклических ссылок
                // Просто передаем feature как есть (это функция или объект)
                const featureData = typeof feature === 'function' ? feature() : feature
                objects.push(featureData)
            })
        }

        // Додаємо конфіг без features, щоб уникнути циклічних посилань
        const { features, ...configWithoutFeatures } = config || {}
        objects.push(configWithoutFeatures)
    })

    if (objects.length === 0) return undefined

    return mergeDeep({}, ...objects)
}
